import type { AnalysisPlatform } from '@/shared/api/types/generated';

// ============================================================================
// Plateforme d'une analyse — TOUTE règle d'affichage propre à Vestiaire se décide
// ici, par `Analysis.platform`, jamais par la forme de l'URL ou des ids (les ids
// VC et Vinted se chevauchent). Plateforme absente (cache ancien) = Vinted.
// ============================================================================

interface PlatformSource {
  platform?: AnalysisPlatform | null;
}

export const isVestiaireAnalysis = (analysis: PlatformSource | null | undefined): boolean =>
  analysis?.platform === 'vestiaire';

interface CriteriaFilterSource extends PlatformSource {
  parsedFilter?: Record<string, unknown> | null;
  vestiaireFilter?: Record<string, unknown> | null;
}

/**
 * Filtre de création de l'analyse, dans l'espace d'ids de SA plateforme : `vestiaireFilter`
 * sur VC (où `parsedFilter` est toujours null), `parsedFilter` sur Vinted.
 */
export const analysisCriteriaFilter = (
  analysis: CriteriaFilterSource | null | undefined,
): Record<string, unknown> | null => {
  if (!analysis) return null;
  return isVestiaireAnalysis(analysis) ? (analysis.vestiaireFilter ?? null) : (analysis.parsedFilter ?? null);
};

/**
 * Bouton « articles similaires » d'un article du feed : toute analyse chargée, SAUF une analyse
 * Vestiaire de PROFIL (`vestiaireFilter` null) — la relance n'y porterait que `sourceProductId`,
 * que l'API refuse (un critère classique est requis, comme sur Vinted). Vinted : inchangé.
 */
export const canSearchSimilarItems = (analysis: CriteriaFilterSource | null | undefined): boolean => {
  if (!analysis) return false;
  return !isVestiaireAnalysis(analysis) || analysis.vestiaireFilter != null;
};

interface RelaunchCriteriaSource {
  relaunchCriteria?: unknown;
  vestiaireRelaunchCriteria?: unknown;
}

/**
 * La relance d'une niche n'existe que si le champ de LA plateforme de l'analyse est rempli
 * (contrat § 4) : celui de l'autre plateforme est toujours null et ne doit jamais faire
 * apparaître un bouton dont la mutation ne partirait pas. Plateforme absente = Vinted.
 */
export const hasPlatformRelaunchCriteria = (
  niche: RelaunchCriteriaSource,
  platform: AnalysisPlatform | null | undefined,
): boolean =>
  isVestiaireAnalysis({ platform }) ? niche.vestiaireRelaunchCriteria != null : niche.relaunchCriteria != null;
